import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdAssessment,
  MdCheckCircle,
  MdCancel,
  MdBarChart,
  MdPieChart,
  MdTrendingUp,
  MdError,
  MdSupervisorAccount,
  MdDateRange,
  MdTableChart,
  MdInventory,
  MdAnalytics,
  MdGrade,
  MdWaterDrop,
  MdWarning,
  MdArrowUpward,
  MdArrowDownward,
  MdRefresh,
  MdDownload,
  MdFilterList,
  MdCalendarToday,
  MdPerson,
  MdFactory
} from "react-icons/md";
import { useNavigate } from 'react-router-dom';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading Dashboard</p>
        <p className="text-sm text-gray-400">Fetching analysis data...</p>
      </div>
    </div>
  </div>
);

const AnalystDashboard = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [piecesData, setPiecesData] = useState([]);
  const [wholesData, setWholesData] = useState([]);

  // Fetch data from both APIs
  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('usibras_login');
      
      // Fetch pieces data
      const piecesResponse = await fetch('https://usibras-web-backend.onrender.com/api/analyst-pieces', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Fetch wholes data
      const wholesResponse = await fetch('https://usibras-web-backend.onrender.com/api/analyst-wholes', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!piecesResponse.ok || !wholesResponse.ok) {
        throw new Error('Failed to fetch analysis data');
      }

      const piecesResult = await piecesResponse.json();
      const wholesResult = await wholesResponse.json();
      
      setPiecesData(piecesResult.data || []);
      setWholesData(wholesResult.data || []);
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      setError('Failed to load analysis data. Please try again later.');
      setPiecesData([]);
      setWholesData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  // Process and combine data for dashboard metrics
  const processedData = React.useMemo(() => {
    const combinedData = [];
    
    // Process pieces data
    piecesData.forEach(item => {
      combinedData.push({
        id: `pieces_${item.id}`,
        type: 'pieces',
        batchCode: item.batch_code,
        product: item.product,
        supervisor: item.supervisor,
        analyst: item.analyst,
        date: item.entry_date,
        moisture: parseFloat(item.moisture || 0),
        recommendation: item.recommendation,
        qualityManager: item.quality_manager,
        observation: item.observation,
        weight: parseFloat(item.weight || 0),
        numMonoblocks: item.num_monoblocks,
        // Quality percentages for pieces
        qualityData: {
          spotted: parseFloat(item.spotted_percentage || 0),
          dessert: parseFloat(item.dessert_percentage || 0),
          rotten: parseFloat(item.rotten_percentage || 0),
          wholes: parseFloat(item.wholes_percentage || 0)
        },
        // Defect percentages for pieces
        defectData: {
          peels: parseFloat(item.peels_percentage || 0),
          broken: parseFloat(item.broken_percentage || 0),
          dirty: parseFloat(item.dirty_percentage || 0),
          clean: parseFloat(item.clean_percentage || 0),
          splits: parseFloat(item.splits_percentage || 0),
          pieces: parseFloat(item.pieces_percentage || 0),
          butts: parseFloat(item.butts_percentage || 0),
          blackPoint: parseFloat(item.black_point_percentage || 0)
        }
      });
    });

    // Process wholes data
    wholesData.forEach(item => {
      combinedData.push({
        id: `wholes_${item.id}`,
        type: 'wholes',
        batchCode: item.batch_code,
        product: item.product,
        supervisor: item.supervisor,
        date: item.analysis_date,
        moisture: parseFloat(item.moisture_percentage || 0),
        recommendation: item.assessment_status,
        qualityManager: item.quality_manager,
        observation: item.observation,
        count: item.count_value,
        numMonoblocks: item.num_monoblocks,
        // Quality percentages for wholes
        qualityData: {
          clean: parseFloat(item.clean_percentage || 0),
          firstQuality: parseFloat(item.first_quality_percentage || 0),
          secondQuality: parseFloat(item.second_quality_percentage || 0),
          thirdQuality: parseFloat(item.third_quality_percentage || 0),
          w3d: parseFloat(item.w3d_percentage || 0),
          w3m: parseFloat(item.w3m_percentage || 0)
        },
        // Defect percentages for wholes
        defectData: {
          broken: parseFloat(item.broken_percentage || 0),
          cut: parseFloat(item.cut_percentage || 0),
          dirty: parseFloat(item.dirty_percentage || 0),
          immature: parseFloat(item.immature_percentage || 0),
          peels: parseFloat(item.peels_percentage || 0),
          rotten: parseFloat(item.rotten_percentage || 0),
          spotted: parseFloat(item.spotted_percentage || 0)
        }
      });
    });

    return combinedData;
  }, [piecesData, wholesData]);
  
  // Filter data based on selected type
  const filteredData = React.useMemo(() => {
    if (selectedType === 'all') return processedData;
    return processedData.filter(item => item.type === selectedType);
  }, [processedData, selectedType]);
  
  // Calculate summary statistics
  const totalAnalyses = filteredData.length;
  const approvedAnalyses = filteredData.filter(a => a.recommendation === 'approved').length;
  const rejectedAnalyses = filteredData.filter(a => a.recommendation === 'rejected').length;
  const pendingAnalyses = filteredData.filter(a => a.recommendation === 'pending').length;
  const approvalRate = totalAnalyses > 0 ? Math.round((approvedAnalyses / totalAnalyses) * 100) : 0;
  
  const avgMoisture = totalAnalyses > 0 ? 
    Math.round(filteredData.reduce((sum, a) => sum + a.moisture, 0) / totalAnalyses * 10) / 10 : 0;
  
  const totalWeight = filteredData.reduce((sum, a) => sum + (a.weight || 0), 0);
  const totalCount = filteredData.reduce((sum, a) => sum + (a.count || 0), 0);

  // Calculate quality metrics for pieces and wholes separately
  const piecesMetrics = React.useMemo(() => {
    const piecesData = filteredData.filter(item => item.type === 'pieces');
    if (piecesData.length === 0) return null;

    const avgQuality = {
      spotted: Math.round(piecesData.reduce((sum, a) => sum + a.qualityData.spotted, 0) / piecesData.length * 10) / 10,
      dessert: Math.round(piecesData.reduce((sum, a) => sum + a.qualityData.dessert, 0) / piecesData.length * 10) / 10,
      rotten: Math.round(piecesData.reduce((sum, a) => sum + a.qualityData.rotten, 0) / piecesData.length * 10) / 10,
      wholes: Math.round(piecesData.reduce((sum, a) => sum + a.qualityData.wholes, 0) / piecesData.length * 10) / 10
    };

    const avgDefects = {
      peels: Math.round(piecesData.reduce((sum, a) => sum + a.defectData.peels, 0) / piecesData.length * 10) / 10,
      broken: Math.round(piecesData.reduce((sum, a) => sum + a.defectData.broken, 0) / piecesData.length * 10) / 10,
      dirty: Math.round(piecesData.reduce((sum, a) => sum + a.defectData.dirty, 0) / piecesData.length * 10) / 10,
      clean: Math.round(piecesData.reduce((sum, a) => sum + a.defectData.clean, 0) / piecesData.length * 10) / 10
    };

    return { avgQuality, avgDefects };
  }, [filteredData]);

  const wholesMetrics = React.useMemo(() => {
    const wholesData = filteredData.filter(item => item.type === 'wholes');
    if (wholesData.length === 0) return null;

    const avgQuality = {
      clean: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.clean, 0) / wholesData.length * 10) / 10,
      firstQuality: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.firstQuality, 0) / wholesData.length * 10) / 10,
      secondQuality: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.secondQuality, 0) / wholesData.length * 10) / 10,
      thirdQuality: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.thirdQuality, 0) / wholesData.length * 10) / 10,
      w3d: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.w3d, 0) / wholesData.length * 10) / 10,
      w3m: Math.round(wholesData.reduce((sum, a) => sum + a.qualityData.w3m, 0) / wholesData.length * 10) / 10
    };

    const avgDefects = {
      broken: Math.round(wholesData.reduce((sum, a) => sum + a.defectData.broken, 0) / wholesData.length * 10) / 10,
      cut: Math.round(wholesData.reduce((sum, a) => sum + a.defectData.cut, 0) / wholesData.length * 10) / 10,
      dirty: Math.round(wholesData.reduce((sum, a) => sum + a.defectData.dirty, 0) / wholesData.length * 10) / 10,
      spotted: Math.round(wholesData.reduce((sum, a) => sum + a.defectData.spotted, 0) / wholesData.length * 10) / 10
    };

    return { avgQuality, avgDefects };
  }, [filteredData]);



  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="mt-5 h-full">
        <Card extra="w-full h-full p-4">
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Failed to Load Dashboard
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchAnalysisData}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-2 sm:mt-5 h-full px-2 sm:px-4 lg:px-6">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-navy-700 dark:text-white mb-2">
              Analyst Process Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Overview of {piecesData.length} pieces and {wholesData.length} wholes analysis records
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/analyst-process/pieces-records')}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
            >
              <MdTableChart className="h-4 w-4" />
              View Pieces Records
            </button>
            <button
              onClick={() => navigate('/admin/analyst-process/wholes-records')}
              className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
            >
              <MdTableChart className="h-4 w-4" />
              View Wholes Records
            </button>
            <button
              onClick={fetchAnalysisData}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              disabled={loading}
            >
              <MdRefresh className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Analyses</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">{totalAnalyses}</p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  {piecesData.length} pieces, {wholesData.length} wholes
                </span>
              </div>
            </div>
            <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
              <MdAnalytics className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Approval Rate</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">{approvalRate}%</p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  {approvedAnalyses} approved, {rejectedAnalyses} rejected
                  {pendingAnalyses > 0 && `, ${pendingAnalyses} pending`}
                </span>
              </div>
            </div>
            <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
              <MdCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Moisture</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">{avgMoisture}%</p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  Across all analyses
                </span>
              </div>
            </div>
            <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
              <MdWaterDrop className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejection Rate</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">{totalAnalyses > 0 ? Math.round((rejectedAnalyses / totalAnalyses) * 100) : 0}%</p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  {rejectedAnalyses} rejected out of {totalAnalyses}
                </span>
              </div>
            </div>
            <div className="rounded-full bg-red-50 p-3 dark:bg-navy-700">
              <MdCancel className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Analysis Type Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 dark:bg-navy-800 p-1 rounded-lg">
          <button
            onClick={() => setSelectedType('all')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedType === 'all'
                ? 'bg-white dark:bg-navy-700 text-navy-700 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white'
            }`}
          >
            All Analyses ({processedData.length})
          </button>
          <button
            onClick={() => setSelectedType('pieces')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedType === 'pieces'
                ? 'bg-white dark:bg-navy-700 text-navy-700 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white'
            }`}
          >
            Pieces Analysis ({piecesData.length})
          </button>
          <button
            onClick={() => setSelectedType('wholes')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedType === 'wholes'
                ? 'bg-white dark:bg-navy-700 text-navy-700 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white'
            }`}
          >
            Wholes Analysis ({wholesData.length})
          </button>
        </div>
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Quality Distribution Chart - Pieces */}
        {(selectedType === 'all' || selectedType === 'pieces') && piecesMetrics && (
        <Card extra="p-6">
          <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Pieces Quality Distribution</h3>
            <MdGrade className="h-5 w-5 text-blue-500" />
          </div>
          <div className="space-y-3">
              {Object.entries(piecesMetrics.avgQuality).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-24 capitalize">{key}</span>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(value, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-navy-700 dark:text-white w-12 text-right">{value}%</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Quality Distribution Chart - Wholes */}
        {(selectedType === 'all' || selectedType === 'wholes') && wholesMetrics && (
          <Card extra="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Wholes Quality Distribution</h3>
              <MdGrade className="h-5 w-5 text-green-500" />
            </div>
            <div className="space-y-3">
              {Object.entries(wholesMetrics.avgQuality).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-24 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(value, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-navy-700 dark:text-white w-12 text-right">{value}%</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Defect Analysis Chart - Pieces */}
        {(selectedType === 'all' || selectedType === 'pieces') && piecesMetrics && (
          <Card extra="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Pieces Defect Analysis</h3>
              <MdWarning className="h-5 w-5 text-red-500" />
            </div>
            <div className="space-y-3">
              {Object.entries(piecesMetrics.avgDefects).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-20 capitalize">{key}</span>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(value * 4, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-navy-700 dark:text-white w-12 text-right">{value}%</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Defect Analysis Chart - Wholes */}
        {(selectedType === 'all' || selectedType === 'wholes') && wholesMetrics && (
          <Card extra="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Wholes Defect Analysis</h3>
              <MdWarning className="h-5 w-5 text-red-500" />
            </div>
            <div className="space-y-3">
              {Object.entries(wholesMetrics.avgDefects).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-20 capitalize">{key}</span>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(value * 4, 100)}%` }}
                    ></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-navy-700 dark:text-white w-12 text-right">{value}%</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>



      {/* Recent Analyses Table */}
      <Card extra="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Recent Analyses</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Showing latest {Math.min(filteredData.length, 10)} records</span>
          </div>
        </div>
        {filteredData.length === 0 ? (
          <div className="flex h-[200px] items-center justify-center">
            <div className="text-center">
              <MdInventory className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-semibold text-gray-700 dark:text-white">No Data Available</p>
              <p className="text-gray-500 dark:text-gray-400">No analysis records found for the selected filter.</p>
            </div>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-navy-700">
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Product</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Batch Code</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Type</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Supervisor</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Moisture</th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Date</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Action</th>
              </tr>
            </thead>
            <tbody>
                {filteredData.slice(0, 10).map((analysis) => (
                <tr key={analysis.id} className="border-b border-gray-100 dark:border-navy-800 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors">
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">{analysis.product}</td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">{analysis.batchCode}</td>
                  <td className="py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        analysis.type === 'pieces'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                        {analysis.type === 'pieces' ? 'Pieces' : 'Wholes'}
                    </span>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">{analysis.supervisor}</td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">{analysis.moisture}%</td>
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(analysis.date).toLocaleDateString()}
                    </td>
                  <td className="py-4">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                      analysis.recommendation === 'approved'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : analysis.recommendation === 'rejected'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                        {analysis.recommendation === 'approved' && <MdCheckCircle className="mr-1 h-3 w-3" />}
                        {analysis.recommendation === 'rejected' && <MdCancel className="mr-1 h-3 w-3" />}
                        {analysis.recommendation === 'pending' && <MdWarning className="mr-1 h-3 w-3" />}
                      {analysis.recommendation.charAt(0).toUpperCase() + analysis.recommendation.slice(1)}
                    </span>
                  </td>
                  <td className="py-4">
                      <button 
                        onClick={() => {
                          if (analysis.type === 'pieces') {
                            navigate('/admin/analyst-process/pieces-records');
                          } else {
                            navigate('/admin/analyst-process/wholes-records');
                          }
                        }}
                        className="rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-600 transition-colors"
                      >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </Card>
    </div>
  );
};

export default AnalystDashboard; 